import { TextLayer } from "deck.gl";
// MultiIconLayer is exported with an underscore prefix to mark it as internal/unstable API.
import type { _MultiIconLayer as MultiIconLayer } from "@deck.gl/layers";
import { useCallback } from "react";

// Mirrors the internal, unexported `Character`/`CharacterMapping` shape from
// @deck.gl/layers's text-layer/utils.ts -- not part of the public API, so we
// keep a local copy rather than deep-importing from dist/.
type Character = {
    x:        number;
    y:        number;
    width:    number;
    height:   number;
    anchorX:  number;
    anchorY:  number;
    advance?: number;
};
type CharacterMapping = Record<string, Character>;

export type FontAdjustProps = {
    getSize:       number;   // world-space text size, default 4
    sizeMinPixels: number;   // default 12
    outlineWidth:  number;   // default 6
    fontWeight:    "normal" | "bold";
    buffer:        number;   // sdf buffer, default 16
    radius:        number;   // sdf radius, default 24
    cutoff:        number;   // sdf cutoff, default 0.20
    smoothing:     number;   // sdf smoothing, default 0.04
};

export const FONT_ADJUST_DEFAULTS: FontAdjustProps = {
    getSize:       4,
    sizeMinPixels: 12,
    outlineWidth:  6,
    fontWeight:    "bold",
    buffer:        16,
    radius:        24,
    cutoff:        0.20,
    smoothing:     0.04,
};

interface Props {
    value: FontAdjustProps;
    onChange: ( v: FontAdjustProps ) => void;
}

export function FontAdjustPanel( { value, onChange }: Props ) {
    const set = useCallback(
        ( key: keyof FontAdjustProps, v: number ) => onChange( { ...value, [key]: v } ),
        [value, onChange]
    );

    const sliders: { key: keyof FontAdjustProps; label: string; min: number; max: number; step: number }[] = [
        { key: "getSize",       label: "Size",          min: 0.5, max: 20,  step: 0.1   },
        { key: "sizeMinPixels", label: "Min Pixels",    min: 1,   max: 48,  step: 1     },
        { key: "outlineWidth",  label: "Outline Width", min: 0,   max: 16,  step: 0.5   },
        { key: "buffer",        label: "SDF Buffer",    min: 0,   max: 32,  step: 1     },
        { key: "radius",        label: "SDF Radius",    min: 1,   max: 48,  step: 1     },
        { key: "cutoff",        label: "SDF Cutoff",    min: 0,   max: 1,   step: 0.01  },
        { key: "smoothing",     label: "SDF Smoothing", min: 0,   max: 0.5, step: 0.005 },
    ];

    return (
        <div
            style={{
                position: "absolute",
                bottom: 16,
                right: 256,
                zIndex: 10,
                background: "rgba(0,0,0,0.75)",
                color: "#fff",
                borderRadius: 8,
                padding: "12px 16px",
                minWidth: 220,
                fontSize: 13,
            }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Font Settings</strong>
                <button
                    className="btn btn-sm btn-outline-light py-0 px-1"
                    style={{ fontSize: 11 }}
                    onClick={() => onChange( FONT_ADJUST_DEFAULTS )}>
                    Reset
                </button>
            </div>
            <div className="mb-2">
                <div className="btn-group btn-group-sm w-100">
                    {( ["normal", "bold"] as const ).map( fw => (
                        <button
                            key={fw}
                            className={`btn ${value.fontWeight === fw ? "btn-light" : "btn-outline-light"}`}
                            style={{ fontSize: 11 }}
                            onClick={() => onChange( { ...value, fontWeight: fw } )}>
                            {fw}
                        </button>
                    ) )}
                </div>
            </div>
            {sliders.map( ( { key, label, min, max, step } ) => (
                <div key={key} className="mb-1">
                    <div className="d-flex justify-content-between">
                        <span>{label}</span>
                        <span style={{ opacity: 0.6 }}>{( value[key] as number ).toFixed(3)}</span>
                    </div>
                    <input
                        type="range"
                        className="w-100"
                        min={min}
                        max={max}
                        step={step}
                        value={value[key] as number}
                        onChange={e => set( key, parseFloat( e.target.value ) )}
                    />
                </div>
            ) )}
        </div>
    );
}

export class PatchedTextLayer<DataT = unknown> extends TextLayer<DataT> {
    static layerName = "PatchedTextLayer";

    renderLayers() {
        const layers = super.renderLayers();
        const mapping: CharacterMapping | undefined = this.state.fontAtlasManager?.mapping;
        const pad = this.props.fontSettings?.buffer ?? 0;
        if( !mapping || pad <= 0 || !this.props.outlineWidth ) { return layers; }

        const expanded: CharacterMapping = {};
        for( const key of Object.keys( mapping ) ) {
            const g = mapping[key];
            expanded[key] = {
                ...g,
                x:       g.x - pad,
                y:       g.y - pad,
                width:   g.width  + 2 * pad,
                height:  g.height + 2 * pad,
                anchorX: g.anchorX + pad,
                anchorY: g.anchorY + pad,
            };
        }

        return layers.map( layer => {
            if( !layer ) { return layer; }
            const multiIconLayer = layer as unknown as MultiIconLayer<DataT>;
            return multiIconLayer.props?.iconMapping === mapping
                ? ( multiIconLayer.clone( { iconMapping: expanded } ) as typeof layer )
                : layer;
        } );
    }
}
